import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { artistsApi } from "../../repositories/artists";
import { Artist } from "../roster/types";

interface ArtistTagInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const ArtistTagInput: React.FC<ArtistTagInputProps> = ({
  value,
  onChange,
  placeholder = "Enter Artist Name"
}) => {
  const [inputValue, setInputValue] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [artists, setArtists] = useState<Artist[]>([]);
  const [filteredArtists, setFilteredArtists] = useState<Artist[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load artists from API
  useEffect(() => {
    const loadArtists = async () => {
      try {
        setIsLoading(true);
        const response = await artistsApi.getAll({ limit: 100 });
        const artistsData = response.data.data.artists || [];
        setArtists(artistsData);
      } catch (error) {
        console.error('Error loading artists:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadArtists();
  }, []);

  // Convert string value to array when component mounts or value changes
  useEffect(() => {
    if (value) {
      const tags = value.split(', ').filter(tag => tag.trim() !== '');
      setSelectedTags(tags);
    } else {
      setSelectedTags([]);
    }
  }, [value]);

  // Filter artists based on input
  useEffect(() => {
    if (inputValue.trim()) {
      const filtered = artists.filter(artist =>
        artist.artistName?.toLowerCase().includes(inputValue.toLowerCase()) &&
        !selectedTags.includes(artist.artistName || '')
      );
      setFilteredArtists(filtered);
    } else {
      setFilteredArtists([]);
    }
  }, [inputValue, artists, selectedTags]);

  // Update parent when tags change
  const updateParent = (newTags: string[]) => {
    const stringValue = newTags.join(', ');
    onChange(stringValue);
  };

  const addTag = (tagName: string) => {
    const trimmedValue = tagName.trim();
    if (trimmedValue && !selectedTags.includes(trimmedValue)) {
      const newTags = [...selectedTags, trimmedValue];
      setSelectedTags(newTags);
      updateParent(newTags);
      setInputValue('');
      setShowDropdown(false);
    }
  };

  const removeTag = (indexToRemove: number) => {
    const newTags = selectedTags.filter((_, index) => index !== indexToRemove);
    setSelectedTags(newTags);
    updateParent(newTags);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredArtists.length > 0) {
        addTag(filteredArtists[0].artistName || '');
      } else if (inputValue.trim()) {
        addTag(inputValue);
      }
    } else if (e.key === 'Backspace' && inputValue === '' && selectedTags.length > 0) {
      removeTag(selectedTags.length - 1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setShowDropdown(true);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  const handleInputFocus = () => {
    setShowDropdown(true);
  };

  const handleInputBlur = () => {
    setTimeout(() => setShowDropdown(false), 200);
  };

  const handleArtistSelect = (artist: Artist) => {
    addTag(artist.artistName || '');
  };

  return (
    <div className="relative w-full">
      {/* Main input container - same styling as ArtistTypeahead */}
      <div className="w-full px-3 py-2 text-sm text-gray-700 border rounded-md focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-500 min-h-[42px] flex flex-wrap gap-1 items-center">
        {selectedTags.map((tag, index) => (
          <span
            key={index}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-md"
          >
            {tag}
            <button
              type="button"
              onClick={() => removeTag(index)}
              className="flex items-center justify-center w-3 h-3 text-blue-600 hover:text-blue-800 hover:bg-blue-200 rounded-full"
            >
              <X size={10} />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={selectedTags.length === 0 ? placeholder : ""}
          className="flex-1 min-w-[100px] outline-none bg-transparent text-sm"
        />
      </div>

      {/* Dropdown - same styling as ArtistTypeahead */}
      {showDropdown && (inputValue || isLoading) && (
        <div
          ref={dropdownRef}
          className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {isLoading ? (
            <div className="px-3 py-2 text-sm text-gray-500">Loading artists...</div>
          ) : filteredArtists.length > 0 ? (
            filteredArtists.map((artist) => (
              <div
                key={artist.id}
                className="px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm"
                onClick={() => handleArtistSelect(artist)}
              >
                <div className="font-medium">{artist.artistName}</div>
              </div>
            ))
          ) : inputValue ? (
            <div
              className="px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm"
              onClick={() => addTag(inputValue)}
            >
              Add "{inputValue}" as new artist
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default ArtistTagInput;